import React from "react";
import { BarChart3, FileText, Home, TrendingUp, Wallet } from "lucide-react";

import OverviewKpiCard from "../companyOverview/OverviewKpiCard";
import { fmtMoney } from "../bexio/bexioFormat";
import { formatCurrency } from "../../../pages/admin/uebersicht/uebersichtHelpers";
import {
  selectCashIn,
  selectContributionMargin,
  selectExpectedContribution,
  selectExpectedRevenue,
  selectInvoiced,
  selectOperatingCosts,
  selectUnclassifiedNotice,
} from "./financeSelectors";

/**
 * The five period figures of the Finanzen tab.
 *
 * FIVE NUMBERS ON FOUR DIFFERENT AXES
 * -----------------------------------
 * They all answer for "the selected period" and they do not mean the same thing
 * by it:
 *
 *   Erwarteter Umsatz  → the contractual SERVICE months
 *   Fakturiert         → the INVOICE date (`issue_date`), which for Vantio is
 *                        typically the month BEFORE the service month
 *   Zahlungseingang    → when the money actually arrived (payment_date)
 *   Betriebskosten     → the selected period
 *   Deckungsbeitrag    → expected revenue MINUS operating costs
 *
 * Under one heading reading "Zeitraum · April – September 2026" they look
 * interchangeable, so every card names its own axis in its subtext. That line is
 * the contract, not decoration.
 *
 * P3c MADE THE SPAN FOLLOW THE SELECTION. The subtexts used to say "im
 * ausgewählten Monat" unconditionally, which was simply unreachable before —
 * the tab refused every range. Now that a range is answerable, the AXIS wording
 * is unchanged and only the span it names moves: invoiced still says
 * `Rechnungsdatum`, cash-in still says `Zahldatum`.
 *
 * THE CARD COMPONENT IS THE OVERVIEW'S
 * ------------------------------------
 * Same tile, same accents, same icons per quantity: "Erwarteter Umsatz" is a
 * blue TrendingUp card on both tabs, because it is the same number. It also
 * brings the three states this row needs for free — `null` renders "—",
 * `error` renders "Nicht verfügbar" with a retry, `loading` renders a skeleton
 * — so a failed request can never appear as CHF 0.
 *
 * NO "GEWINN". This row states expected contractual revenue minus operating
 * cost. That is a contribution margin, not a result: it contains no one-off
 * costs, no write-offs and no cash reality. The tab it replaces called the same
 * subtraction "Gewinn" and derived a margin from it.
 */

/**
 * A franc amount, or `null` when there is no figure.
 *
 * `formatCurrency` is the repo's CHF formatter and is reused verbatim — but it
 * maps null/undefined to "CHF 0" (`Number(null || 0)`), which is precisely the
 * claim a missing figure must not make. So the null decision happens HERE,
 * before formatting, and `OverviewKpiCard` renders "—" for it.
 */
const chf = (value) =>
  value === null || value === undefined ? null : formatCurrency(value);

/** The margin as one decimal, e.g. "40.3 %". Never built when the base is null. */
const pct = (value) =>
  value === null || value === undefined
    ? null
    : `${new Intl.NumberFormat("de-CH", { maximumFractionDigits: 1 }).format(value)} %`;

/**
 * The span a period figure names, as the end of a sentence.
 *
 * A single month reads exactly as it always did; a window says so. The
 * alternative — a card that says "Monat" under a heading reading
 * "April – September 2026" — is the dishonesty this slice removes, and it is not
 * fixed by changing the number alone.
 */
const span = (isRange) => (isRange ? "im gewählten Zeitraum" : "im ausgewählten Monat");

export default function FinanceKpiGrid({
  revenueAnalytics,
  companyInvoiced,
  costBreakdown,
  window: periodWindow,
  loading = false,
  onRetry,
}) {
  const isRange = Boolean(periodWindow?.isRange);
  const expected = selectExpectedRevenue(revenueAnalytics?.data);
  // A1, deliberately: the company total on the issue_date axis. NOT
  // `revenue-analytics.kpis.invoiced_total`, which counts only the invoices the
  // draft ledger attributes to this RENT month — a minority subset in
  // production, wearing the same German label.
  const invoiced = selectInvoiced(companyInvoiced?.data);
  const cashIn = selectCashIn(revenueAnalytics?.data);
  // A2's own total. The five buckets are NOT re-added here: the backend rounds
  // each independently, so a frontend sum can miss the contract total, and it
  // would silently become a different quantity the day a bucket is added.
  const costs = selectOperatingCosts(costBreakdown?.data);
  const contribution = selectExpectedContribution(expected, costs);
  const margin = selectContributionMargin(expected, contribution);
  const unclassified = selectUnclassifiedNotice(costBreakdown?.data);

  // Two upstreams, so the contribution is unavailable when EITHER fails — and
  // it must not fall back to "expected", which would look like a superb month.
  const contributionError =
    revenueAnalytics?.error || costBreakdown?.error ? "unavailable" : "";

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
      <OverviewKpiCard
        testId="finance-kpi-expected-revenue"
        title="Erwarteter Umsatz"
        value={chf(expected)}
        subtext={`Vertrags-Soll ${span(isRange)}`}
        icon={<TrendingUp className="h-4 w-4" />}
        accent="blue"
        compact
        loading={loading}
        error={revenueAnalytics?.error}
        onRetry={onRetry}
      />
      <OverviewKpiCard
        testId="finance-kpi-invoiced"
        title="Fakturiert"
        value={chf(invoiced)}
        subtext={`Rechnungsdatum ${span(isRange)}`}
        icon={<FileText className="h-4 w-4" />}
        accent="indigo"
        compact
        loading={loading}
        error={companyInvoiced?.error}
        onRetry={onRetry}
      />
      <OverviewKpiCard
        testId="finance-kpi-cash-in"
        title="Zahlungseingang"
        value={chf(cashIn)}
        subtext={`Nach Zahldatum ${span(isRange)}`}
        icon={<Wallet className="h-4 w-4" />}
        accent="sky"
        compact
        loading={loading}
        error={revenueAnalytics?.error}
        onRetry={onRetry}
      />
      <OverviewKpiCard
        testId="finance-kpi-operating-costs"
        title="Betriebskosten"
        value={chf(costs)}
        subtext={`Klassifizierte Betriebskosten ${span(isRange)}`}
        icon={<Home className="h-4 w-4" />}
        accent="amber"
        compact
        loading={loading}
        error={costBreakdown?.error}
        onRetry={onRetry}
        footer={
          // Outside the headline by contract. Stated neutrally, not as a
          // warning: an unrecognised cost type is a fact about the data, and
          // production's free-text "Kaution" row is exactly why it is shown
          // apart instead of being counted as an operating expense.
          unclassified.present ? (
            <p className="m-0 text-[11px] leading-snug text-muted-foreground">
              Nicht klassifiziert: {fmtMoney(unclassified.raw)}
              {unclassified.count > 0
                ? ` · ${unclassified.count} ${
                    unclassified.count === 1 ? "Position" : "Positionen"
                  }`
                : ""}
            </p>
          ) : null
        }
      />
      <OverviewKpiCard
        testId="finance-kpi-expected-contribution"
        title="Erwarteter Deckungsbeitrag"
        value={chf(contribution)}
        subtext="Soll-Miete minus Betriebskosten"
        icon={<BarChart3 className="h-4 w-4" />}
        accent="emerald"
        compact
        loading={loading}
        error={contributionError}
        onRetry={onRetry}
        footer={
          // Omitted whenever there is no defined percentage — including a zero
          // expected revenue, where "0 %" would read as a real and very bad
          // margin rather than as "there is nothing to take a share of".
          margin === null ? null : (
            <p className="m-0 text-[11px] leading-snug text-muted-foreground">
              Deckungsbeitragsmarge: {pct(margin)}
            </p>
          )
        }
      />
    </div>
  );
}
